// File: /opt/dev_environment/assets/workspace/app-init/src/context/VocabularyContext.js

import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { cardsApi, reviewsApi, settingsApi, dataApi } from '../services/apiClient';
import { settingsLocalCache, resetLocalData } from '../services/storage';
import { LEITNER_COMPARTMENTS, TRAINING_MODES } from '../constants/leitner';

const VocabularyContext = createContext(null);

const ACTIONS = {
  LOAD_DATA: 'LOAD_DATA',
  LOAD_ERROR: 'LOAD_ERROR',
  SET_CARDS: 'SET_CARDS',
  SET_VERB_CARDS: 'SET_VERB_CARDS',
  SET_LEARNED_WORDS: 'SET_LEARNED_WORDS',
  SET_LEARNED_VERBS: 'SET_LEARNED_VERBS',
  SET_REVIEWS: 'SET_REVIEWS',
  ADD_CARD: 'ADD_CARD',
  UPDATE_CARD: 'UPDATE_CARD',
  DELETE_CARD: 'DELETE_CARD',
  ADD_VERB_CARDS: 'ADD_VERB_CARDS',
  ADD_VERB_CARD: 'ADD_VERB_CARD',
  UPDATE_VERB_CARD: 'UPDATE_VERB_CARD',
  DELETE_VERB_CARD: 'DELETE_VERB_CARD',
  RECORD_REVIEW: 'RECORD_REVIEW',
  GRADUATE_CARD: 'GRADUATE_CARD',
  SET_DIRECTION: 'SET_DIRECTION',
  SET_TRAINING_MODE: 'SET_TRAINING_MODE',
  SELECT_COMPARTMENT: 'SELECT_COMPARTMENT',
  TOGGLE_SHUFFLE: 'TOGGLE_SHUFFLE',
  SET_SHUFFLED_CARDS: 'SET_SHUFFLED_CARDS',
  ADVANCE_CARD_INDEX: 'ADVANCE_CARD_INDEX',
  SET_CARD_INDEX: 'SET_CARD_INDEX',
  AUTO_ADVANCE_COMPARTMENT: 'AUTO_ADVANCE_COMPARTMENT',
  SET_ALL_REVIEWED: 'SET_ALL_REVIEWED',
  RESET_ALL_DATA: 'RESET_ALL_DATA',
  MARK_CARD_REVIEWED: 'MARK_CARD_REVIEWED',
  CLEAR_REVIEWED_CARDS: 'CLEAR_REVIEWED_CARDS',
};

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function getActiveCards(state) {
  if (state.trainingMode === TRAINING_MODES.VERBEN) {
    return state.verbCards;
  }
  return state.cards;
}

/**
 * Normalize a card object from the API (snake_case) to frontend (camelCase).
 */
function normalizeCard(apiCard) {
  if (!apiCard) return null;
  return {
    id: apiCard.id,
    type: apiCard.type || 'vocabulary',
    status: apiCard.status || 'active',
    compartment: apiCard.compartment,
    germanWord: apiCard.german_word,
    italianWord: apiCard.italian_word,
    gender: apiCard.gender,
    category: apiCard.category,
    topicTag: apiCard.topic_tag,
    baseVerb: apiCard.base_verb,
    germanInfinitive: apiCard.german_infinitive,
    verbGroup: apiCard.verb_group,
    conjugationForm: apiCard.conjugation_form,
    conjugationFormLabel: apiCard.conjugation_form_label,
    conjugationFormGermanLabel: apiCard.conjugation_form_german_label,
    targetPerson: apiCard.target_person,
    targetPersonLabel: apiCard.target_person_label,
    targetPersonGermanLabel: apiCard.target_person_german_label,
    targetFormDescription: apiCard.target_form_description,
    targetFormDescriptionGerman: apiCard.target_form_description_german,
    conjugatedAnswer: apiCard.conjugated_answer,
    germanConjugation: apiCard.german_conjugation,
    createdAt: apiCard.created_at,
    lastReviewedAt: apiCard.last_reviewed_at,
    nextReviewAt: apiCard.next_review_at,
    learnedAt: apiCard.learned_at,
  };
}

/**
 * Normalize a review object from the API.
 */
function normalizeReview(apiReview) {
  if (!apiReview) return null;
  return {
    id: apiReview.id,
    cardId: apiReview.card_id,
    cardType: apiReview.card_type,
    correct: apiReview.correct,
    direction: apiReview.direction,
    timestamp: apiReview.timestamp,
  };
}

/**
 * Convert a frontend card object to API format (camelCase → snake_case).
 */
function toApiCard(frontendCard) {
  const apiCard = {};
  if (frontendCard.type !== undefined) apiCard.type = frontendCard.type;
  if (frontendCard.germanWord !== undefined) apiCard.german_word = frontendCard.germanWord;
  if (frontendCard.italianWord !== undefined) apiCard.italian_word = frontendCard.italianWord;
  if (frontendCard.gender !== undefined) apiCard.gender = frontendCard.gender;
  if (frontendCard.category !== undefined) apiCard.category = frontendCard.category;
  if (frontendCard.topicTag !== undefined) apiCard.topic_tag = frontendCard.topicTag;
  if (frontendCard.baseVerb !== undefined) apiCard.base_verb = frontendCard.baseVerb;
  if (frontendCard.germanInfinitive !== undefined) apiCard.german_infinitive = frontendCard.germanInfinitive;
  if (frontendCard.verbGroup !== undefined) apiCard.verb_group = frontendCard.verbGroup;
  if (frontendCard.conjugationForm !== undefined) apiCard.conjugation_form = frontendCard.conjugationForm;
  if (frontendCard.conjugationFormLabel !== undefined) apiCard.conjugation_form_label = frontendCard.conjugationFormLabel;
  if (frontendCard.conjugationFormGermanLabel !== undefined) apiCard.conjugation_form_german_label = frontendCard.conjugationFormGermanLabel;
  if (frontendCard.targetPerson !== undefined) apiCard.target_person = frontendCard.targetPerson;
  if (frontendCard.targetPersonLabel !== undefined) apiCard.target_person_label = frontendCard.targetPersonLabel;
  if (frontendCard.targetPersonGermanLabel !== undefined) apiCard.target_person_german_label = frontendCard.targetPersonGermanLabel;
  if (frontendCard.targetFormDescription !== undefined) apiCard.target_form_description = frontendCard.targetFormDescription;
  if (frontendCard.targetFormDescriptionGerman !== undefined) apiCard.target_form_description_german = frontendCard.targetFormDescriptionGerman;
  if (frontendCard.conjugatedAnswer !== undefined) apiCard.conjugated_answer = frontendCard.conjugatedAnswer;
  if (frontendCard.germanConjugation !== undefined) apiCard.german_conjugation = frontendCard.germanConjugation;
  return apiCard;
}

/**
 * Convert an array of frontend verb card data objects to API format for bulk creation.
 */
function toApiVerbCards(frontendCards) {
  return frontendCards.map((card) => ({
    type: 'verb',
    base_verb: card.baseVerb,
    german_infinitive: card.germanInfinitive || '',
    verb_group: card.verbGroup,
    conjugation_form: card.conjugationForm,
    conjugation_form_label: card.conjugationFormLabel,
    conjugation_form_german_label: card.conjugationFormGermanLabel || '',
    target_person: card.targetPerson,
    target_person_label: card.targetPersonLabel,
    target_person_german_label: card.targetPersonGermanLabel || '',
    target_form_description: card.targetFormDescription,
    target_form_description_german: card.targetFormDescriptionGerman || '',
    conjugated_answer: card.conjugatedAnswer,
    german_conjugation: card.germanConjugation || '',
  }));
}

const initialState = {
  cards: [],
  verbCards: [],
  learnedWords: [],
  learnedVerbs: [],
  reviews: [],
  settings: { queryDirection: 'IT→DE', trainingMode: 'vokabeln' },
  isLoaded: false,
  loadError: null,
  trainingMode: TRAINING_MODES.VOKABELN,
  selectedCompartment: 1,
  shuffleActive: false,
  shuffledCardIds: [],
  shufflePromptMessage: null,
  currentCardIndex: 0,
  allReviewed: false,
  reviewedCardIds: [],
};

function vocabularyReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD_DATA: {
      const loadedCompartment = action.payload.settings.selectedCompartment;
      return {
        ...state,
        cards: action.payload.cards,
        verbCards: action.payload.verbCards,
        learnedWords: action.payload.learnedWords,
        learnedVerbs: action.payload.learnedVerbs,
        reviews: action.payload.reviews,
        settings: action.payload.settings,
        trainingMode: action.payload.settings.trainingMode || TRAINING_MODES.VOKABELN,
        selectedCompartment: loadedCompartment != null ? loadedCompartment : 1,
        shuffleActive: action.payload.settings.shuffleActive || false,
        isLoaded: true,
        loadError: null,
      };
    }

    case ACTIONS.LOAD_ERROR: {
      return {
        ...state,
        isLoaded: true,
        loadError: action.payload,
      };
    }

    case ACTIONS.SET_CARDS: {
      return { ...state, cards: action.payload };
    }

    case ACTIONS.SET_VERB_CARDS: {
      return { ...state, verbCards: action.payload };
    }

    case ACTIONS.SET_LEARNED_WORDS: {
      return { ...state, learnedWords: action.payload };
    }

    case ACTIONS.SET_LEARNED_VERBS: {
      return { ...state, learnedVerbs: action.payload };
    }

    case ACTIONS.SET_REVIEWS: {
      return { ...state, reviews: action.payload };
    }

    case ACTIONS.ADD_CARD: {
      return {
        ...state,
        cards: [...state.cards, action.payload],
      };
    }

    case ACTIONS.UPDATE_CARD: {
      return {
        ...state,
        cards: state.cards.map((c) =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        ),
      };
    }

    case ACTIONS.DELETE_CARD: {
      return {
        ...state,
        cards: state.cards.filter((c) => c.id !== action.payload),
      };
    }

    case ACTIONS.ADD_VERB_CARDS: {
      return {
        ...state,
        verbCards: [...state.verbCards, ...action.payload],
      };
    }

    case ACTIONS.ADD_VERB_CARD: {
      return {
        ...state,
        verbCards: [...state.verbCards, action.payload],
      };
    }

    case ACTIONS.UPDATE_VERB_CARD: {
      return {
        ...state,
        verbCards: state.verbCards.map((c) =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        ),
      };
    }

    case ACTIONS.DELETE_VERB_CARD: {
      return {
        ...state,
        verbCards: state.verbCards.filter((c) => c.id !== action.payload),
      };
    }

    case ACTIONS.RECORD_REVIEW: {
      const { updatedCard, review, cardType } = action.payload;
      const isVerb = cardType === 'verb';

      const newState = {
        ...state,
        reviews: [...state.reviews, review],
      };

      if (updatedCard) {
        if (isVerb) {
          newState.verbCards = state.verbCards.map((c) =>
            c.id === updatedCard.id ? updatedCard : c
          );
        } else {
          newState.cards = state.cards.map((c) =>
            c.id === updatedCard.id ? updatedCard : c
          );
        }
      }

      return newState;
    }

    case ACTIONS.GRADUATE_CARD: {
      const { graduatedCard, cardType } = action.payload;
      const isVerb = cardType === 'verb';

      if (isVerb) {
        return {
          ...state,
          verbCards: state.verbCards.filter((c) => c.id !== graduatedCard.id),
          learnedVerbs: [...state.learnedVerbs, graduatedCard],
        };
      } else {
        return {
          ...state,
          cards: state.cards.filter((c) => c.id !== graduatedCard.id),
          learnedWords: [...state.learnedWords, graduatedCard],
        };
      }
    }

    case ACTIONS.SET_DIRECTION: {
      return {
        ...state,
        settings: { ...state.settings, queryDirection: action.payload },
      };
    }

    case ACTIONS.SET_TRAINING_MODE: {
      const mode = action.payload;
      return {
        ...state,
        trainingMode: mode,
        selectedCompartment: 1,
        shuffleActive: false,
        shuffledCardIds: [],
        currentCardIndex: 0,
        allReviewed: false,
        reviewedCardIds: [],
      };
    }

    case ACTIONS.SELECT_COMPARTMENT: {
      const compartment = action.payload;
      return {
        ...state,
        selectedCompartment: compartment,
        shuffleActive: false,
        shuffledCardIds: [],
        currentCardIndex: 0,
        allReviewed: false,
        reviewedCardIds: [],
      };
    }

    case ACTIONS.TOGGLE_SHUFFLE: {
      if (!state.selectedCompartment) {
        return {
          ...state,
          shufflePromptMessage: 'Bitte zuerst ein Fach auswählen',
        };
      }

      const newShuffleActive = !state.shuffleActive;

      let shuffledCardIds = [];
      if (newShuffleActive) {
        const activeCards = getActiveCards(state);
        const compartmentCards = activeCards.filter(
          (c) => c.compartment === state.selectedCompartment
        );
        shuffledCardIds = shuffleArray(compartmentCards).map((c) => c.id);
      }

      return {
        ...state,
        shuffleActive: newShuffleActive,
        shuffledCardIds,
        shufflePromptMessage: null,
        currentCardIndex: 0,
        reviewedCardIds: [],
      };
    }

    case ACTIONS.SET_SHUFFLED_CARDS: {
      return {
        ...state,
        shuffledCardIds: action.payload,
      };
    }

    case ACTIONS.ADVANCE_CARD_INDEX: {
      return {
        ...state,
        currentCardIndex: state.currentCardIndex + 1,
      };
    }

    case ACTIONS.SET_CARD_INDEX: {
      return {
        ...state,
        currentCardIndex: action.payload,
      };
    }

    case ACTIONS.AUTO_ADVANCE_COMPARTMENT: {
      const newCompartment = action.payload;

      let shuffledCardIds = [];
      if (state.shuffleActive) {
        const activeCards = getActiveCards(state);
        const compartmentCards = activeCards.filter(
          (c) => c.compartment === newCompartment
        );
        shuffledCardIds = shuffleArray(compartmentCards).map((c) => c.id);
      }

      return {
        ...state,
        selectedCompartment: newCompartment,
        currentCardIndex: 0,
        shuffledCardIds,
        allReviewed: false,
        reviewedCardIds: [],
      };
    }

    case ACTIONS.SET_ALL_REVIEWED: {
      return {
        ...state,
        allReviewed: true,
      };
    }

    case ACTIONS.MARK_CARD_REVIEWED: {
      return {
        ...state,
        reviewedCardIds: [...state.reviewedCardIds, action.payload],
      };
    }

    case ACTIONS.CLEAR_REVIEWED_CARDS: {
      return {
        ...state,
        reviewedCardIds: [],
      };
    }

    case ACTIONS.RESET_ALL_DATA: {
      return {
        ...initialState,
        isLoaded: true,
        cards: action.payload?.cards || [],
        verbCards: [],
        learnedWords: [],
        learnedVerbs: [],
        reviews: [],
      };
    }

    default:
      return state;
  }
}

export function VocabularyProvider({ children }) {
  const [state, dispatch] = useReducer(vocabularyReducer, initialState);
  const isLoadingRef = useRef(false);

  // ---- Core data fetching function ----
  const fetchAllData = useCallback(async () => {
    // Prevent concurrent loads
    if (isLoadingRef.current) {
      console.log('[Vokabelkasten] Load already in progress, skipping');
      return;
    }
    isLoadingRef.current = true;

    try {
      const [
        vocabActiveRes,
        vocabLearnedRes,
        verbActiveRes,
        verbLearnedRes,
        reviewsRes,
        settingsRes,
      ] = await Promise.all([
        cardsApi.getAll({ type: 'vocabulary', status: 'active' }),
        cardsApi.getAll({ type: 'vocabulary', status: 'learned' }),
        cardsApi.getAll({ type: 'verb', status: 'active' }),
        cardsApi.getAll({ type: 'verb', status: 'learned' }),
        reviewsApi.getAll({ limit: 1000 }),
        settingsApi.get().catch(() => null),
      ]);

      const cards = (vocabActiveRes.cards || []).map(normalizeCard);
      const learnedWords = (vocabLearnedRes.cards || []).map(normalizeCard);
      const verbCards = (verbActiveRes.cards || []).map(normalizeCard);
      const learnedVerbs = (verbLearnedRes.cards || []).map(normalizeCard);
      const reviews = (reviewsRes.reviews || []).map(normalizeReview);

      const localSettings = settingsLocalCache.get();
      const apiSettings = settingsRes || {};
      const settings = {
        queryDirection: apiSettings.query_direction || localSettings.queryDirection || 'IT→DE',
        trainingMode: apiSettings.training_mode || localSettings.trainingMode || 'vokabeln',
        selectedCompartment: apiSettings.selected_compartment ?? localSettings.selectedCompartment ?? 1,
        shuffleActive: apiSettings.shuffle_active ?? localSettings.shuffleActive ?? false,
      };

      settingsLocalCache.save(settings);

      dispatch({
        type: ACTIONS.LOAD_DATA,
        payload: { cards, verbCards, learnedWords, learnedVerbs, reviews, settings },
      });

      console.log('[Vokabelkasten] Data loaded:', {
        vocabActive: cards.length,
        vocabLearned: learnedWords.length,
        verbActive: verbCards.length,
        verbLearned: learnedVerbs.length,
        reviews: reviews.length,
      });
    } catch (error) {
      console.error('[Vokabelkasten] Failed to load data from API:', error);
      dispatch({ type: ACTIONS.LOAD_ERROR, payload: error.message });
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  // ---- Initial data load ----
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // ---- Shuffle sync ----
  useEffect(() => {
    if (state.shuffleActive && state.selectedCompartment && state.isLoaded) {
      const activeCards = getActiveCards(state);
      const compartmentCards = activeCards.filter(
        (c) => c.compartment === state.selectedCompartment
      );
      const currentIds = new Set(compartmentCards.map((c) => c.id));
      const shuffledStillValid = state.shuffledCardIds.every((id) => currentIds.has(id))
        && state.shuffledCardIds.length === compartmentCards.length;
      if (!shuffledStillValid && compartmentCards.length > 0) {
        dispatch({
          type: ACTIONS.SET_SHUFFLED_CARDS,
          payload: shuffleArray(compartmentCards).map((c) => c.id),
        });
      }
    }
  }, [state.cards, state.verbCards, state.shuffleActive, state.selectedCompartment, state.isLoaded, state.shuffledCardIds, state.trainingMode]);

  // ============================================
  // CARD OPERATIONS (async, API-backed)
  // ============================================

  const addCard = useCallback(async (cardData) => {
    try {
      const apiData = toApiCard({ ...cardData, type: 'vocabulary' });
      apiData.type = 'vocabulary';
      const result = await cardsApi.create(apiData);
      const newCards = (result.cards || []).map(normalizeCard);
      if (newCards.length > 0) {
        dispatch({ type: ACTIONS.ADD_CARD, payload: newCards[0] });
      }
      return newCards[0];
    } catch (error) {
      console.error('[Vokabelkasten] Failed to add card:', error);
      throw error;
    }
  }, []);

  const updateCard = useCallback(async (id, updates) => {
    try {
      const apiUpdates = toApiCard(updates);
      const result = await cardsApi.update(id, apiUpdates);
      const updatedCard = normalizeCard(result.card || result);
      dispatch({ type: ACTIONS.UPDATE_CARD, payload: updatedCard });
      return updatedCard;
    } catch (error) {
      console.error('[Vokabelkasten] Failed to update card:', error);
      throw error;
    }
  }, []);

  const deleteCard = useCallback(async (id) => {
    try {
      await cardsApi.delete(id);
      dispatch({ type: ACTIONS.DELETE_CARD, payload: id });
    } catch (error) {
      console.error('[Vokabelkasten] Failed to delete card:', error);
      throw error;
    }
  }, []);

  const addVerbCards = useCallback(async (cardDataArray) => {
    try {
      const apiCards = toApiVerbCards(cardDataArray);
      const result = await cardsApi.create(apiCards);
      const newCards = (result.cards || []).map(normalizeCard);
      if (newCards.length > 0) {
        dispatch({ type: ACTIONS.ADD_VERB_CARDS, payload: newCards });
      }
      return newCards;
    } catch (error) {
      console.error('[Vokabelkasten] Failed to add verb cards:', error);
      throw error;
    }
  }, []);

  const addVerbCard = useCallback(async (cardData) => {
    try {
      const apiData = toApiVerbCards([cardData])[0];
      const result = await cardsApi.create(apiData);
      const newCards = (result.cards || []).map(normalizeCard);
      if (newCards.length > 0) {
        dispatch({ type: ACTIONS.ADD_VERB_CARD, payload: newCards[0] });
      }
      return newCards[0];
    } catch (error) {
      console.error('[Vokabelkasten] Failed to add verb card:', error);
      throw error;
    }
  }, []);

  const updateVerbCard = useCallback(async (id, updates) => {
    try {
      const apiUpdates = toApiCard(updates);
      const result = await cardsApi.update(id, apiUpdates);
      const updatedCard = normalizeCard(result.card || result);
      dispatch({ type: ACTIONS.UPDATE_VERB_CARD, payload: updatedCard });
      return updatedCard;
    } catch (error) {
      console.error('[Vokabelkasten] Failed to update verb card:', error);
      throw error;
    }
  }, []);

  const deleteVerbCard = useCallback(async (id) => {
    try {
      await cardsApi.delete(id);
      dispatch({ type: ACTIONS.DELETE_VERB_CARD, payload: id });
    } catch (error) {
      console.error('[Vokabelkasten] Failed to delete verb card:', error);
      throw error;
    }
  }, []);

  // ============================================
  // REVIEW & GRADUATION (async, API-backed)
  // ============================================

  const recordReview = useCallback(async (cardId, correct, direction, cardType = 'vocabulary') => {
    try {
      const result = await cardsApi.review(cardId, { correct, direction });
      const updatedCard = result.card ? normalizeCard(result.card) : null;
      const review = result.review ? normalizeReview(result.review) : {
        cardId,
        correct,
        direction,
        cardType,
        timestamp: new Date().toISOString(),
      };

      dispatch({
        type: ACTIONS.RECORD_REVIEW,
        payload: { updatedCard, review, cardType },
      });
    } catch (error) {
      console.error('[Vokabelkasten] Failed to record review:', error);
      throw error;
    }
  }, []);

  const graduateCard = useCallback(async (cardId, cardType = 'vocabulary') => {
    try {
      const result = await cardsApi.graduate(cardId, {});
      const graduatedCard = normalizeCard(result.card || result);

      dispatch({
        type: ACTIONS.GRADUATE_CARD,
        payload: { graduatedCard, cardType },
      });
    } catch (error) {
      console.error('[Vokabelkasten] Failed to graduate card:', error);
      throw error;
    }
  }, []);

  // ============================================
  // SETTINGS (async API + local cache)
  // ============================================

  const setDirection = useCallback(async (direction) => {
    dispatch({ type: ACTIONS.SET_DIRECTION, payload: direction });
    settingsLocalCache.update({ queryDirection: direction });

    settingsApi.update({ query_direction: direction }).catch((err) => {
      console.error('[Vokabelkasten] Failed to sync direction setting:', err);
    });
  }, []);

  const setTrainingMode = useCallback(async (mode) => {
    dispatch({ type: ACTIONS.SET_TRAINING_MODE, payload: mode });
    settingsLocalCache.update({ trainingMode: mode, selectedCompartment: 1 });

    settingsApi.update({ training_mode: mode, selected_compartment: 1 }).catch((err) => {
      console.error('[Vokabelkasten] Failed to sync training mode:', err);
    });
  }, []);

  const selectCompartment = useCallback(async (compartment) => {
    dispatch({ type: ACTIONS.SELECT_COMPARTMENT, payload: compartment });
    settingsLocalCache.update({ selectedCompartment: compartment, shuffleActive: false });

    settingsApi.update({ selected_compartment: compartment, shuffle_active: false }).catch((err) => {
      console.error('[Vokabelkasten] Failed to sync compartment selection:', err);
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    dispatch({ type: ACTIONS.TOGGLE_SHUFFLE });

    const newShuffleActive = !state.shuffleActive;
    settingsLocalCache.update({ shuffleActive: newShuffleActive });

    settingsApi.update({ shuffle_active: newShuffleActive }).catch((err) => {
      console.error('[Vokabelkasten] Failed to sync shuffle state:', err);
    });
  }, [state.shuffleActive]);

  // ============================================
  // UI STATE (local only, no API)
  // ============================================

  const advanceCardIndex = useCallback(() => {
    dispatch({ type: ACTIONS.ADVANCE_CARD_INDEX });
  }, []);

  const setCardIndex = useCallback((index) => {
    dispatch({ type: ACTIONS.SET_CARD_INDEX, payload: index });
  }, []);

  const autoAdvanceCompartment = useCallback((compartment) => {
    dispatch({ type: ACTIONS.AUTO_ADVANCE_COMPARTMENT, payload: compartment });
    settingsLocalCache.update({ selectedCompartment: compartment });

    settingsApi.update({ selected_compartment: compartment }).catch((err) => {
      console.error('[Vokabelkasten] Failed to sync auto-advance:', err);
    });
  }, []);

  const setAllReviewed = useCallback(() => {
    dispatch({ type: ACTIONS.SET_ALL_REVIEWED });
  }, []);

  const markCardReviewed = useCallback((cardId) => {
    dispatch({ type: ACTIONS.MARK_CARD_REVIEWED, payload: cardId });
  }, []);

  const clearReviewedCards = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_REVIEWED_CARDS });
  }, []);

  // ============================================
  // RELOAD — re-fetch everything from API
  // Returns a promise that resolves when dispatch is done
  // ============================================

  const reloadData = useCallback(async () => {
    console.log('[Vokabelkasten] reloadData called — fetching fresh data from API');
    await fetchAllData();
    console.log('[Vokabelkasten] reloadData complete — context state updated');
  }, [fetchAllData]);

  // ============================================
  // RESET (async, API-backed)
  // ============================================

  const resetAll = useCallback(async () => {
    try {
      const result = await dataApi.reset({ confirm: true, reseed: true });
      resetLocalData();

      let seedCards = [];
      if (result.reseeded) {
        const res = await cardsApi.getAll({ type: 'vocabulary', status: 'active' });
        seedCards = (res.cards || []).map(normalizeCard);
      }

      dispatch({ type: ACTIONS.RESET_ALL_DATA, payload: { cards: seedCards } });
    } catch (error) {
      console.error('[Vokabelkasten] Failed to reset data:', error);
      throw error;
    }
  }, []);

  // ============================================
  // COMPUTED VALUES (derived from local state)
  // ============================================

  const getActiveCardsForMode = useCallback(() => {
    if (state.trainingMode === TRAINING_MODES.VERBEN) {
      return state.verbCards;
    }
    return state.cards;
  }, [state.cards, state.verbCards, state.trainingMode]);

  const getDueCards = useCallback((compartment = null) => {
    const now = new Date();
    const activeCards = getActiveCardsForMode();
    return activeCards.filter((card) => {
      if (compartment && card.compartment !== compartment) return false;
      return new Date(card.nextReviewAt) <= now;
    });
  }, [getActiveCardsForMode]);

  const getCompartmentCounts = useCallback(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const activeCards = getActiveCardsForMode();
    activeCards.forEach((card) => {
      if (counts[card.compartment] !== undefined) {
        counts[card.compartment]++;
      }
    });
    return counts;
  }, [getActiveCardsForMode]);

  const getCardsForSelectedCompartment = useCallback(() => {
    if (!state.selectedCompartment) return [];

    const activeCards = getActiveCardsForMode();
    const reviewedSet = new Set(state.reviewedCardIds);
    const compartmentCards = activeCards.filter(
      (c) => c.compartment === state.selectedCompartment && !reviewedSet.has(c.id)
    );

    if (state.shuffleActive && state.shuffledCardIds.length > 0) {
      const cardMap = new Map(compartmentCards.map((c) => [c.id, c]));
      return state.shuffledCardIds
        .map((id) => cardMap.get(id))
        .filter(Boolean);
    }

    return compartmentCards;
  }, [getActiveCardsForMode, state.selectedCompartment, state.shuffleActive, state.shuffledCardIds, state.reviewedCardIds]);

  const getProgress = useCallback(() => {
    if (state.trainingMode === TRAINING_MODES.VERBEN) {
      const totalActive = state.verbCards.length;
      const totalLearned = state.learnedVerbs.length;
      const total = totalActive + totalLearned;
      if (total === 0) return { total: 0, learned: totalLearned, active: totalActive, percentage: 0 };
      return {
        total,
        learned: totalLearned,
        active: totalActive,
        percentage: Math.round((totalLearned / total) * 100),
      };
    }
    const totalActive = state.cards.length;
    const totalLearned = state.learnedWords.length;
    const total = totalActive + totalLearned;
    if (total === 0) return { total: 0, learned: totalLearned, active: totalActive, percentage: 0 };
    return {
      total,
      learned: totalLearned,
      active: totalActive,
      percentage: Math.round((totalLearned / total) * 100),
    };
  }, [state.cards, state.verbCards, state.learnedWords, state.learnedVerbs, state.trainingMode]);

  const getCardErrorRate = useCallback((cardId) => {
    const cardReviews = state.reviews.filter((r) => r.cardId === cardId);
    if (cardReviews.length === 0) return { total: 0, errors: 0, rate: 0 };
    const errors = cardReviews.filter((r) => !r.correct).length;
    return {
      total: cardReviews.length,
      errors,
      rate: Math.round((errors / cardReviews.length) * 100),
    };
  }, [state.reviews]);

  const findNextCompartmentWithCards = useCallback((currentCompartment) => {
    const activeCards = getActiveCardsForMode();
    const reviewedSet = new Set(state.reviewedCardIds);
    for (let i = 1; i <= 5; i++) {
      const nextComp = ((currentCompartment - 1 + i) % 5) + 1;
      const hasCards = activeCards.some((c) => c.compartment === nextComp && !reviewedSet.has(c.id));
      if (hasCards) {
        return nextComp;
      }
    }
    return null;
  }, [getActiveCardsForMode, state.reviewedCardIds]);

  const hasVerbCardsForGroupAndForm = useCallback((group, form) => {
    return state.verbCards.some((c) => c.verbGroup === group && c.conjugationForm === form);
  }, [state.verbCards]);

  const value = {
    ...state,
    addCard,
    updateCard,
    deleteCard,
    addVerbCards,
    addVerbCard,
    updateVerbCard,
    deleteVerbCard,
    recordReview,
    graduateCard,
    setDirection,
    setTrainingMode,
    selectCompartment,
    toggleShuffle,
    advanceCardIndex,
    setCardIndex,
    autoAdvanceCompartment,
    setAllReviewed,
    markCardReviewed,
    clearReviewedCards,
    resetAll,
    reloadData,
    getDueCards,
    getCompartmentCounts,
    getCardsForSelectedCompartment,
    getProgress,
    getCardErrorRate,
    findNextCompartmentWithCards,
    getActiveCardsForMode,
    hasVerbCardsForGroupAndForm,
  };

  return (
    <VocabularyContext.Provider value={value}>
      {children}
    </VocabularyContext.Provider>
  );
}

export function useVocabulary() {
  const context = useContext(VocabularyContext);
  if (!context) {
    throw new Error('useVocabulary must be used within a VocabularyProvider');
  }
  return context;
}

export default VocabularyContext;